/**
 * ActivePlanView — wraps MealPlanCard inside a section landmark for the dashboard.
 *
 * Security (CLAUDE.md §4): delegates entirely to MealPlanCard, which uses safe text nodes.
 *
 * Accessibility (CLAUDE.md §7, spec tracking-dashboard):
 *   - <section aria-labelledby="active-plan-heading">
 *   - h2 section heading
 *   - Empty / loading / error states handled independently
 *   - Reuses MealPlanCard accessible rendering
 *   - "Descargar PDF" button: min-h-[44px], aria-label, loading/disabled states,
 *     aria-live error region (plan-pdf-export §UI)
 *
 * States: loading / error / empty (no plan) / plan.
 * PDF button: visible only when plan exists and pdfEnabled !== false.
 */

import { useState, useCallback } from "react";
import type { MealPlan } from "@autodieta/shared";
import { MealPlanCard } from "../chat/MealPlanCard.js";
import { downloadPlanPdf, PdfDownloadError } from "./planPdf.js";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ActivePlanViewProps {
  /** The active meal plan. Undefined = no active plan. */
  plan?: MealPlan;
  /** When true, renders loading skeleton. */
  loading?: boolean;
  /** When set, renders error state. */
  error?: string;
  /** Worker base URL for PDF download. Required to show PDF button. */
  workerUrl?: string;
  /** Returns a fresh Firebase ID token. Required to show PDF button. */
  getIdToken?: () => Promise<string | null>;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const HEADING_ID = "active-plan-heading";

export function ActivePlanView({
  plan,
  loading,
  error,
  workerUrl,
  getIdToken,
}: ActivePlanViewProps): JSX.Element {
  return (
    <section aria-labelledby={HEADING_ID}>
      <h2 id={HEADING_ID} className="mb-3 text-base font-semibold text-zinc-100">
        Plan activo
      </h2>
      <ActivePlanContent
        // exactOptionalPropertyTypes: use conditional spread to avoid passing undefined to optional props
        {...(plan !== undefined ? { plan } : {})}
        {...(loading !== undefined ? { loading } : {})}
        {...(error !== undefined ? { error } : {})}
        {...(workerUrl !== undefined ? { workerUrl } : {})}
        {...(getIdToken !== undefined ? { getIdToken } : {})}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Inner content
// ---------------------------------------------------------------------------

interface ActivePlanContentProps {
  plan?: MealPlan;
  loading?: boolean;
  error?: string;
  workerUrl?: string;
  getIdToken?: () => Promise<string | null>;
}

function ActivePlanContent({
  plan,
  loading,
  error,
  workerUrl,
  getIdToken,
}: ActivePlanContentProps): JSX.Element {
  if (loading) {
    return <MealPlanCard loading />;
  }

  if (error) {
    return <MealPlanCard error={error} />;
  }

  if (!plan) {
    return (
      <p className="py-4 text-center text-sm text-zinc-500">
        Todavía no tenés un plan activo — pedile uno al agente en el Chat.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <MealPlanCard mealPlan={plan} />
      {workerUrl !== undefined && getIdToken !== undefined ? (
        <PdfDownloadButton
          workerUrl={workerUrl}
          getIdToken={getIdToken}
          planId={plan.id}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PDF Download Button
// ---------------------------------------------------------------------------

interface PdfDownloadButtonProps {
  workerUrl: string;
  getIdToken: () => Promise<string | null>;
  planId: string;
}

function PdfDownloadButton({
  workerUrl,
  getIdToken,
  planId,
}: PdfDownloadButtonProps): JSX.Element {
  const [downloading, setDownloading] = useState(false);
  const [pdfError, setPdfError] = useState<string | undefined>(undefined);

  const handleDownload = useCallback(async (): Promise<void> => {
    setDownloading(true);
    setPdfError(undefined);

    try {
      const token = await getIdToken();
      if (!token) {
        setPdfError("No se pudo obtener el token de autenticacion.");
        return;
      }

      await downloadPlanPdf({
        workerUrl,
        getIdToken: async () => token,
        planId,
      });
    } catch (err) {
      if (err instanceof PdfDownloadError) {
        if (err.status === 404) {
          setPdfError("No se encontro el plan nutricional.");
        } else if (err.status === 401) {
          setPdfError("No autorizado. Por favor, vuelva a iniciar sesion.");
        } else {
          setPdfError("No se pudo descargar el PDF. Intente de nuevo.");
        }
      } else {
        setPdfError("Error inesperado al descargar el PDF.");
      }
    } finally {
      setDownloading(false);
    }
  }, [workerUrl, getIdToken, planId]);

  return (
    <div>
      <button
        type="button"
        aria-label={
          downloading ? "Descargando plan nutricional en PDF..." : "Descargar plan nutricional en PDF"
        }
        disabled={downloading}
        onClick={() => void handleDownload()}
        className="min-h-[44px] w-full rounded-lg border border-zinc-700 bg-zinc-800 px-4 py-2 text-sm font-medium text-zinc-200 transition-colors hover:bg-zinc-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {downloading ? "Descargando..." : "Descargar PDF"}
      </button>
      {/* aria-live region for error feedback */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="mt-1 min-h-[1.25rem] text-xs text-red-400"
      >
        {pdfError ?? ""}
      </div>
    </div>
  );
}
